/*
 * Input modality: hides the focus ring after pointer/touch input, shows it again on keyboard input
 */

const setInputModality = (modality) => {
  document.documentElement.dataset.inputModality = modality;
};

document.addEventListener('pointerdown', () => setInputModality('pointer'), { capture: true, passive: true });
document.addEventListener('keydown', () => setInputModality('keyboard'), { capture: true });

/*
 * Focus Trap helpers
 */

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

/* ---------------------------------------
 * Focus‑trap helpers
 ----------------------------------------*/

// store all handler fns *and* run‑time data in one object
const trapFocusHandlers = {};

// will hold the element that had focus *before* the trap
let previouslyFocusedElement = null;

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a:not([tabindex^='-']), button:enabled:not([tabindex^='-']), [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container) {
  // Remove any existing trap first
  removeTrapFocus();

  // Remember what was focused *before* we trap (if it's outside)
  const current = document.activeElement;
  previouslyFocusedElement =
    current && !container.contains(current) ? current : null;

  /* ---------- calculate first / last focusable ---------- */
  function setFocusableElements() {
    const elements = getFocusableElements(container);
    trapFocusHandlers.first = elements[0];
    trapFocusHandlers.last = elements[elements.length - 1];
  }
  setFocusableElements();   // initialise once

  /* ---------- listeners ---------- */
  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== trapFocusHandlers.last &&
      event.target !== trapFocusHandlers.first
    ) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () =>
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

  trapFocusHandlers.keydown = (event) => {
    if (!event.code || event.code.toUpperCase() !== 'TAB') return;

    // TAB forward on the last element  → loop to first
    if (event.target === trapFocusHandlers.last && !event.shiftKey) {
      event.preventDefault();
      trapFocusHandlers.first?.focus();
    }

    // SHIFT+TAB on the first element → loop to last
    if (
      (event.target === container || event.target === trapFocusHandlers.first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      trapFocusHandlers.last?.focus();
    }
  };

  /* ---------- attach listeners ---------- */
  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  /* ----------  Watch for DOM changes  ---------- */
  const observer = new MutationObserver(() => {
    setFocusableElements();                  // refresh first / last

    // If focus was lost because the element was removed, restore it:
    if (!container.contains(document.activeElement)) {
      trapFocusHandlers.first?.focus();
    }
  });

  observer.observe(container, { childList: true, subtree: true });

  /* expose a way to stop observing when the trap is removed */
  trapFocusHandlers.disconnect = () => observer.disconnect();

  /* ---------- expose a public "refresh" method ---------- */
  trapFocusHandlers.update = setFocusableElements;

  /* ---------- move initial focus ---------- */
  trapFocusHandlers.first?.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  trapFocusHandlers.disconnect?.();

  // prefer the explicit argument, otherwise fall back to previously‑focused element
  if (!elementToFocus && previouslyFocusedElement?.isConnected) {
    elementToFocus = previouslyFocusedElement;
  }

  if (elementToFocus) elementToFocus.focus();

  // clean up
  previouslyFocusedElement = null;
  delete trapFocusHandlers.first;
  delete trapFocusHandlers.last;
  delete trapFocusHandlers.update;
}

/**
 * Validates if a URL belongs to YouTube or Vimeo by parsing and checking the hostname.
 * This prevents security risks from malicious URLs that might contain these strings.
 * @param {string} url - The URL to validate
 * @returns {Object|null} - Object with type: 'youtube'|'vimeo' or null if invalid
 */
function validateVideoUrl(url) {
  if (!url || typeof url !== 'string') return null;

  try {
    // Parse the URL to extract the hostname
    const urlObj = new URL(url, window.location.origin);
    const hostname = urlObj.hostname.toLowerCase();

    // Allowed YouTube domains (including subdomains)
    const youtubeDomains = [
      'youtube.com',
      'www.youtube.com',
      'youtu.be',
      'www.youtu.be',
      'youtube-nocookie.com',
      'www.youtube-nocookie.com'
    ];

    // Allowed Vimeo domains (including subdomains)
    const vimeoDomains = [
      'vimeo.com',
      'www.vimeo.com',
      'player.vimeo.com'
    ];

    // Check if hostname matches any allowed domain (exact match or subdomain)
    for (const domain of youtubeDomains) {
      if (hostname === domain || hostname.endsWith('.' + domain)) {
        return { type: 'youtube' };
      }
    }

    for (const domain of vimeoDomains) {
      if (hostname === domain || hostname.endsWith('.' + domain)) {
        return { type: 'vimeo' };
      }
    }

    return null;
  } catch (e) {
    // Invalid URL format
    return null;
  }
}

/* ---------------------------------------
 * Other helpers
 ----------------------------------------*/

// Markup for a media-control icon (e.g. 'play' / 'pause'), sourced once from the theme icon system.
function mediaControlIcon(name) {
  return (window.themeIcons && window.themeIcons[name]) || '';
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, wait) {
  let t = false;
  return (...args) => {
    if (!t) {
      fn.apply(this, args)
      t = true;
      setTimeout(() => t = false, wait)
    }
  }
}

/*
 * Marshall Common JS
 */

// Initialize window.Marshall object
if (typeof window.Marshall == 'undefined') {
  window.Marshall = {};
}
Marshall.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

// -------
// Load components / libraries
Marshall.init = function () {
  Marshall.ModalsAndDrawers.init();
  Marshall.Header.init();
  Marshall.Megamenu.init();
  Marshall.HeaderSearchOverlay.init();
  Marshall.DetailsAccordions.init();
  Marshall.Discounts.init();
  Marshall.Cart.init();
  Marshall.Media.initReducedMotion();
  Marshall.ProductCards.init();

  // Always-visible play/pause toggle on animated GIFs and controls-less autoplay videos
  // (independent of reduced motion).
  Marshall.Media.initGifToggles(document);
  Marshall.Media.initVideoToggles(document);
  Marshall.StackedTables.init(document);
  document.addEventListener('shopify:section:load', (event) => {
    Marshall.Media.initGifToggles(event.target);
    Marshall.Media.initVideoToggles(event.target);
    Marshall.StackedTables.init(event.target);
    Marshall.ProductCards.sync(event.target);
    Marshall.ProductCards.bindHoverImages(event.target);
  });

  // Attach theme editor listeners
  if (!Shopify || !Shopify.designMode) return;

  let lastSelection = {
    sectionId: undefined,
    blockId: undefined
  };

  // Load is called when something is changed within section settings
  document.addEventListener('shopify:section:load', (evt) => {
    const sectionId = 'shopify-section-' + evt.detail.sectionId
    if (sectionId === Marshall.Header.cache.section?.id) {
      Marshall.ModalsAndDrawers.init();
      Marshall.Header.init();
      Marshall.Megamenu.init();
      Marshall.HeaderSearchOverlay.init();
    }

    if (document.querySelector(`[data-modal][data-section-id="${evt.detail.sectionId}"]`)) {
      Marshall.ModalsAndDrawers.showModalOrDrawer(document.querySelector(`[data-modal][data-section-id="${evt.detail.sectionId}"]`).id);
    }
  })

  document.addEventListener('shopify:block:select', (evt) => {
    const sectionId = 'shopify-section-' + evt.detail.sectionId
    const sectionEl = document.getElementById(sectionId)
    if (!sectionEl) {
      return;
    }
    const blocks = sectionEl.querySelectorAll('[data-shopify-editor-block]')
    const selectedBlock = [...blocks].find(x => JSON.parse(x.dataset.shopifyEditorBlock).id === evt.detail.blockId);
    if (sectionId === Marshall.Header.cache.section?.id) {
      if (selectedBlock) {
        Marshall.ModalsAndDrawers.showModalOrDrawer('drawer-menu');

        const accordionItem = selectedBlock.closest('[data-accordion-item]');
        const accordion = accordionItem?.querySelector('input');
        const label = accordionItem?.querySelector('label');
        if (accordion && label && !accordion.checked) {
          label.click();
        }
      }
    } else if (sectionId.includes('modal') && sectionEl.querySelector('[data-modal]')) {
      if (selectedBlock) {
        Marshall.ModalsAndDrawers.showModalOrDrawer(sectionEl.querySelector('[data-modal]').id);
      }
    }

    lastSelection.sectionId = evt.detail.sectionId;
    lastSelection.blockId = evt.detail.blockId;
  })

  document.addEventListener('shopify:section:select', (evt) => {
    lastSelection = {
      sectionId: evt.detail.sectionId,
      blockId: evt.detail.blockId
    }

    if (evt.detail.sectionId.includes('modal') && document.querySelector(`[data-modal][data-section-id="${evt.detail.sectionId}"]`)) {
      Marshall.ModalsAndDrawers.showModalOrDrawer(document.querySelector(`[data-modal][data-section-id="${evt.detail.sectionId}"]`).id);
    }
  })

  document.addEventListener('shopify:section:deselect', (evt) => {
    lastSelection = {
      sectionId: undefined,
      blockId: undefined
    }

    if (evt.detail.sectionId.includes('modal') && document.querySelector(`[data-modal][data-section-id="${evt.detail.sectionId}"]`)) {
      Marshall.ModalsAndDrawers.closeModalOrDrawer(document.querySelector(`[data-modal][data-section-id="${evt.detail.sectionId}"]`).id);
    }
  })
};

// -------
// Marshall Common JS - Utility library
Marshall.Utils = {
  debugMode: true,
  prepareQueryParams: () => {
    Shopify.queryParams = Shopify.queryParams || {};

    // Preserve existing query parameters
    if (location.search.length) {
      const params = location.search.substr(1).split('&');
      for (let i = 0; i < params.length; i++) {
        const keyValue = params[i].split('=');
        // Spaces are reverted back after the decode, since in component-facets.js we'll use URLSearchParams() method and we don't want to double decode the spaces
        if (keyValue.length) {
          Shopify.queryParams[decodeURIComponent(keyValue[0])] = decodeURIComponent(keyValue[1]).replaceAll('+', ' ');
        }
      }
    }

    return Shopify.queryParams;
  },
  formatMoney(cents, format, applyCurrencyRate = true) {
    const default_money_format = theme.settings.moneyFormat || '€{{amount}}';
    if (typeof cents == 'string') {
      cents = cents.replace('.', '');
    }
    if (applyCurrencyRate && window?.Shopify?.currency?.rate) {
      cents = cents * parseFloat(window.Shopify.currency.rate || 1.0);
    }

    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || default_money_format;

    function defaultOption(opt, def) {
      return typeof opt == 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  },
  useShopMoneyFormat(currencyCode, countryCode) {
    const currencyFormat = theme.settings.currencyFormat || {};

    if (!currencyFormat.currencyCodeEnabled) {
      return true;
    }

    const code =
      currencyCode || currencyFormat.activeCurrencyIsoCode || window.Shopify?.currency?.active || '';
    const country = countryCode || currencyFormat.countryIsoCode || '';

    if (code === 'EUR') {
      return true;
    }

    if (code === 'USD' && country !== 'SG') {
      return true;
    }

    return false;
  },
  formatMoneyDisplay(cents, format, applyCurrencyRate = true) {
    const currencyFormat = theme.settings.currencyFormat || {};
    const code =
      currencyFormat.activeCurrencyIsoCode || window.Shopify?.currency?.active || '';

    if (Marshall.Utils.useShopMoneyFormat(code)) {
      return Marshall.Utils.formatMoney(cents, format, applyCurrencyRate);
    }

    const withoutCurrencyFormat =
      currencyFormat.moneyWithoutCurrencyFormat || theme.settings.moneyFormat;
    const amount = Marshall.Utils.formatMoney(cents, withoutCurrencyFormat, applyCurrencyRate);

    return `${amount} ${code}`;
  },
};

// -------
// Marshall Common JS - Header component
Marshall.Header = {
  selectors: {
    header: 'MainHeader',
    cartBlip: '.cart-blip',
    megamenuHamburger: '[data-megamenu-hamburger]'
  },
  cache: {
    header: undefined,
    cartBlip: undefined,
    section: undefined,
    resizeObserver: null,
    headerHeight: undefined
  },

  /** Primary nav / megamenu breakpoint: matches CSS that hides desktop menubar below 1024px. */
  isTinyLayout: function () {
    return window.matchMedia('(max-width: 1023px)').matches;
  },

  /** Sync hamburger expanded state with drawer/megamenu visibility. */
  syncHeaderMenuToggleState: function (isOpen) {
    const open = typeof isOpen === 'boolean'
      ? isOpen
      : Boolean(document.getElementById('drawer-menu')?.classList.contains('is-open'))
        || Boolean(document.querySelector('[data-megamenu]')?.classList.contains('is-open'));
    const siteHeaderGroup = document.querySelector('.site-header-group');
    if (siteHeaderGroup) {
      siteHeaderGroup.classList.toggle('drawer-menu-open', open);
    }
    document.querySelectorAll(this.selectors.megamenuHamburger).forEach((button) => {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  },

  /**
   * Menu (hamburger) control: mobile opens/closes drawer-menu; desktop toggles megamenu first panel.
   */
  bindMegamenuHamburger: function () {
    document.querySelectorAll(this.selectors.megamenuHamburger).forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const megamenuModule = Marshall.Megamenu;
        if (this.isTinyLayout()) {
          const drawer = document.getElementById('drawer-menu');
          if (drawer?.classList.contains('is-open')) {
            // Route through the shared close so media paused on open resumes and drawer
            // state is emitted, matching every other drawer close path.
            Marshall.ModalsAndDrawers.closeModalOrDrawer('drawer-menu');
          } else {
            Marshall.ModalsAndDrawers.showModalOrDrawer('drawer-menu');
          }
          return;
        }
        const megamenuEl =
          megamenuModule.cache.megamenu ||
          document.querySelector(megamenuModule.selectors.megamenu);
        if (!megamenuEl) return;
        if (megamenuEl.classList.contains('is-open')) {
          megamenuModule.close();
        } else {
          const defaultHandle =
            megamenuEl.dataset.megamenuDefaultPanel || megamenuEl.dataset.megamenuFirstPanel;
          if (defaultHandle) megamenuModule.open(defaultHandle);
        }
      });
    });
  },

  /** Initialize header behaviors, observers, and event bindings. */
  init: function () {
    this.cache.header = document.getElementById(this.selectors.header);
    if (!this.cache.header) return;
    this.cache.cartBlip = this.cache.header.querySelector(this.selectors.cartBlip);
    this.cache.section = this.cache.header.closest('.shopify-section');
    this.cache.headerHeight = undefined;

    // Coalesce to at most one layout pass per frame so resizing tracks the viewport live.
    let layoutFrame = 0;
    const syncHeaderLayout = () => {
      if (layoutFrame) return;
      layoutFrame = requestAnimationFrame(() => {
        layoutFrame = 0;
        Marshall.layoutHeader(this.cache.header);
        this.setHeaderHeightVar();
      });
    };

    if (this.cache.resizeObserver) {
      this.cache.resizeObserver.disconnect();
      this.cache.resizeObserver = null;
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.cache.resizeObserver = new ResizeObserver(syncHeaderLayout);
      this.cache.resizeObserver.observe(this.cache.header);
    }

    syncHeaderLayout();

    // Update cart blip when product is added to cart.
    document.addEventListener('ajaxProduct:added', this.updateCartBlip.bind(this))
    document.addEventListener('cart:update', this.updateCartBlip.bind(this));

    this.bindMegamenuHamburger();

    if (this.cache.header.dataset.stickyBehavior === 'none') return;
    this.initStickyBehavior(this.cache.header.dataset.stickyBehavior)
  },

  /** Keep CSS header height variables in sync with the rendered header. */
  setHeaderHeightVar: function () {
    if (!this.cache.header) return;
    const areaOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-area-offset')) || 0;
    const headerHeight = this.cache.header.offsetHeight + areaOffset;
    if (headerHeight === this.cache.headerHeight) return;
    this.cache.headerHeight = headerHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    if (document.documentElement.classList.contains('sticky-header-initialized')) {
      document.documentElement.style.setProperty('--sticky-header-margin-top', `${headerHeight}px`);
    }
  },

  /** Apply sticky header show/hide behavior based on scroll direction. */
  initStickyBehavior(behaviorType) {
    document.documentElement.classList.add('sticky-header-initialized', `sticky-header-${behaviorType}`);
    this.setHeaderHeightVar();

    const stickyConfig = {
      headerHeight: this.cache.header.offsetHeight,
      hideThreshold: 25,
      behavior: behaviorType
    }

    if (behaviorType === 'scroll' || behaviorType === 'fixed') {
      // Hide after threshold when scrolling down, show when scrolling up.
      let prevScroll = window.scrollY || document.documentElement.scrollTop;

      window.addEventListener('scroll', throttle(() => {
        const curScroll = window.scrollY || document.documentElement.scrollTop;

        if (curScroll <= stickyConfig.hideThreshold) {
          document.documentElement.classList.remove('sticky-header-hide');
          prevScroll = curScroll;
          return;
        }

        if (curScroll > prevScroll) {
          document.documentElement.classList.add('sticky-header-hide');
        } else if (curScroll < prevScroll) {
          document.documentElement.classList.remove('sticky-header-hide');
        }

        prevScroll = curScroll
      }, 50))
    }
  },

  // Update the cart blip when the cart is updated
  async updateCartBlip(e) {
    const cart = e.detail && e.detail.cart && e.detail.cart.item_count || await (async function () {
      const res = await fetch('/cart.json');
      const cart = await res.json();
      return cart;
    })();

    if (!this.cache.cartBlip) {
      return;
    }
    this.cache.cartBlip.textContent = cart.item_count || 0;
    const cartLink = this.cache.cartBlip.closest('a.header__link-item, a.header-item__link');
    if (cart && cart.item_count == 0) {
      this.cache.cartBlip.setAttribute('hidden', true);
      cartLink?.classList.remove('has-blip-visible');
    } else {
      this.cache.cartBlip.removeAttribute('hidden');
      cartLink?.classList.add('has-blip-visible');
    }

    // Update notification cart amount at the same time (if present)
    const total_in_notification = document.querySelector('#cart-notification-link .cart-total');
    if (total_in_notification) {
      total_in_notification.textContent = cart.item_count;
    }
  }
};

// -------
// Megamenu: open full-width panel on parent link click (desktop only)
Marshall.Megamenu = {
  selectors: {
    megamenu: '[data-megamenu]',
    trigger: '[data-megamenu-trigger]',
    panel: '[data-megamenu-panel]',
    close: '[data-megamenu-close]',
    extras: '.megamenu__products, .megamenu__newsletter, .megamenu-support-chat'
  },
  cache: {
    megamenu: null,
    header: null
  },

  /** Update aria-expanded on top-level triggers based on active panel. */
  setTriggerExpanded: function (activeParentHandle) {
    document.querySelectorAll(this.selectors.trigger).forEach((trigger) => {
      const expanded = activeParentHandle && trigger.dataset.megamenuParent === activeParentHandle;
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  },

  /** Reset panel active/transition states and unhide optional extras. */
  resetPanels: function () {
    if (!this.cache.megamenu) return;
    this.cache.megamenu.querySelectorAll(this.selectors.panel).forEach((panel) => {
      panel.classList.remove('is-active', 'is-visible', 'is-leaving');
      panel.setAttribute('aria-hidden', 'true');
      this.setExtrasHidden(panel, false);
    });
  },

  /** Toggle visibility and aria-hidden for optional panel extras. */
  setExtrasHidden: function (panel, hidden) {
    if (!panel) return;
    panel.querySelectorAll(this.selectors.extras).forEach((block) => {
      block.hidden = hidden;
      block.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    });
  },

  /** Hide extras only when they are clipped in the visible megamenu viewport. */
  syncExtras: function () {
    if (!this.cache.megamenu) return;
    const activePanel = this.cache.megamenu.querySelector(`${this.selectors.panel}.is-active`);
    if (!activePanel) return;

    requestAnimationFrame(() => {
      const container = this.cache.megamenu?.querySelector('.megamenu__container');
      const extras = Array.from(activePanel.querySelectorAll(this.selectors.extras));
      if (extras.length === 0) return;

      if (!container || Marshall.Header.isTinyLayout()) {
        this.setExtrasHidden(activePanel, false);
        return;
      }

      // Temporarily keep extras invisible (but measurable) to avoid flicker on open.
      extras.forEach((block) => {
        block.hidden = false;
        block.setAttribute('aria-hidden', 'false');
        block.style.visibility = 'hidden';
      });

      const wrapRect = container.getBoundingClientRect();
      const visibleTop = Math.max(wrapRect.top, 0);
      const visibleBottom = Math.min(wrapRect.bottom, window.innerHeight);
      const clipped = extras.some((block) => {
        const blockRect = block.getBoundingClientRect();
        return blockRect.top < visibleTop || blockRect.bottom > visibleBottom;
      });

      this.setExtrasHidden(activePanel, clipped);
      extras.forEach((block) => {
        block.style.removeProperty('visibility');
      });
    });
  },

  /** Open a panel, animate panel switching, and expose megamenu container. */
  open: function (parentHandle) {
    if (!this.cache.megamenu) return;
    const nextPanel = this.cache.megamenu.querySelector(`${this.selectors.panel}[data-megamenu-panel="${parentHandle}"]`);
    if (!nextPanel) return;

    // Snapshot and pause page media on the first open; a no-op when switching panels.
    if (!this.cache.megamenu.classList.contains('is-open')) {
      Marshall.Media.pausePlayingMedia(document, { keepAnnouncementsBarPlaying: true });
    }

    const prevPanel = this.cache.megamenu.querySelector(`${this.selectors.panel}.is-active`);
    const isSwitching = prevPanel && prevPanel !== nextPanel;
    const transitionMs = 220;

    if (isSwitching) {
      prevPanel.classList.add('is-leaving');
      let done = false;
      const onDone = () => {
        if (done) return;
        done = true;
        prevPanel.classList.remove('is-active', 'is-leaving', 'is-visible');
        prevPanel.setAttribute('aria-hidden', 'true');
        nextPanel.classList.add('is-active');
        nextPanel.setAttribute('aria-hidden', 'false');
        this.syncExtras();
        requestAnimationFrame(() => nextPanel.classList.add('is-visible'));
      };
      prevPanel.addEventListener('transitionend', onDone, { once: true });
      setTimeout(onDone, transitionMs);
    } else {
      this.resetPanels();
      nextPanel.classList.add('is-active');
      nextPanel.setAttribute('aria-hidden', 'false');
      this.syncExtras();
      requestAnimationFrame(() => nextPanel.classList.add('is-visible'));
    }

    this.cache.megamenu.classList.add('is-open');
    this.cache.megamenu.setAttribute('aria-hidden', 'false');
    if (this.cache.megamenu.parentElement) {
      this.cache.megamenu.parentElement.classList.add('megamenu-open');
    }
    document.body.classList.add('overflow-hidden');
    this.cache.megamenu.querySelectorAll(this.selectors.close).forEach(el => el.removeAttribute('aria-hidden'));
    this.setTriggerExpanded(parentHandle);
    Marshall.Header.syncHeaderMenuToggleState();
    this.syncExtras();
    setTimeout(() => this.syncExtras(), 180);
  },

  /** Close megamenu and restore default aria/state attributes. */
  close: function () {
    if (!this.cache.megamenu) return;

    // Resume only the media that was playing when the menu opened.
    Marshall.Media.resumePausedMedia();

    this.cache.megamenu.classList.remove('is-open');
    this.cache.megamenu.setAttribute('aria-hidden', 'true');
    if (this.cache.megamenu.parentElement) {
      this.cache.megamenu.parentElement.classList.remove('megamenu-open');
    }
    document.body.classList.remove('overflow-hidden');
    this.resetPanels();
    this.setTriggerExpanded(null);
    this.cache.megamenu.querySelectorAll(this.selectors.close).forEach(el => el.setAttribute('aria-hidden', 'true'));
    Marshall.Header.syncHeaderMenuToggleState();
  },

  /** Visible focusables inside an open megamenu panel (DOM order). */
  getPanelFocusables: function (panel) {
    if (!panel) return [];
    return Array.from(
      panel.querySelectorAll(
        'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([type="hidden"]):not([disabled])'
      )
    ).filter((el) => {
      if (el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
      if (el.closest('[aria-hidden="true"]')) return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 || rect.height > 0;
    });
  },

  focusFirstInPanel: function (parentHandle) {
    const panel = this.cache.megamenu?.querySelector(
      `${this.selectors.panel}[data-megamenu-panel="${parentHandle}"]`
    );
    const list = this.getPanelFocusables(panel);
    if (list[0]) list[0].focus();
  },

  /** Primary nav links, then utility cluster (support, search, cart, menu toggle), in DOM order. */
  getHeaderMenuRowItems: function (menuRow) {
    const nav = menuRow.querySelector('nav');
    const secondary = menuRow.querySelector('.header__menu-secondary');
    const primaryItems = nav
      ? Array.from(nav.querySelectorAll(':scope > a.header__link-item'))
      : [];
    const secondaryItems = secondary
      ? Array.from(
          secondary.querySelectorAll(':scope > a.header__link-item, :scope > button.header__menu-toggle')
        ).filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null)
      : [];
    return primaryItems.concat(secondaryItems);
  },

  /** Keyboard support for horizontal top-row nav and panel entry. */
  onHeaderMenuKeydown: function (e) {
    const menuRow = this.cache.header?.querySelector('.header__menu');
    if (!menuRow || !menuRow.contains(e.target)) return;

    const rowItems = this.getHeaderMenuRowItems(menuRow);
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';
    const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';
    const prevKey = rtl ? 'ArrowRight' : 'ArrowLeft';

    if (e.key === nextKey || e.key === prevKey) {
      const current = rowItems.find((el) => el === e.target || el.contains(e.target));
      if (!current) return;
      e.preventDefault();
      const i = rowItems.indexOf(current);
      const delta = e.key === nextKey ? 1 : -1;
      const next = rowItems[i + delta];
      if (next) next.focus();
      return;
    }

    const current = e.target.closest('a.header__link-item');
    if (!current || !rowItems.includes(current)) return;

    /* Down opens nothing; when the panel is already open, move focus into it (Enter still opens). */
    if (e.key === 'ArrowDown' && current.hasAttribute('data-megamenu-trigger')) {
      if (!this.cache.megamenu?.classList.contains('is-open')) {
        return;
      }
      const handle = current.dataset.megamenuParent;
      const panel = this.cache.megamenu.querySelector(
        `${this.selectors.panel}[data-megamenu-panel="${handle}"]`
      );
      if (!panel?.classList.contains('is-active')) {
        return;
      }
      e.preventDefault();
      this.focusFirstInPanel(handle);
    }
  },

  /** Keyboard support for vertical focus movement inside the active panel. */
  onMegamenuPanelKeydown: function (e) {
    if (!this.cache.megamenu?.classList.contains('is-open')) return;
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    const panel = e.target.closest('[data-megamenu-panel]');
    if (!panel || !panel.classList.contains('is-active')) return;
    const focusables = this.getPanelFocusables(panel);
    if (focusables.length === 0) return;

    const current = focusables.find((el) => el === e.target || el.contains(e.target));
    if (!current) return;
    const idx = focusables.indexOf(current);

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx === 0) {
        const handle = panel.getAttribute('data-megamenu-panel');
        const trigger = document.querySelector(
          `[data-megamenu-trigger][data-megamenu-parent="${CSS.escape(handle)}"]`
        );
        trigger?.focus();
        return;
      }
      focusables[idx - 1].focus();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (idx < focusables.length - 1) {
        focusables[idx + 1].focus();
      }
    }
  },

  /** Initialize megamenu interactions, keyboard navigation, and layout listeners. */
  init: function () {
    this.cache.megamenu = document.querySelector(this.selectors.megamenu);
    this.cache.header = document.getElementById(Marshall.Header.selectors.header);
    if (!this.cache.megamenu || !this.cache.header) return;

    const menuRow = this.cache.header.querySelector('.header__menu');
    if (menuRow) {
      menuRow.addEventListener('keydown', this.onHeaderMenuKeydown.bind(this));
    }
    this.cache.megamenu.addEventListener('keydown', this.onMegamenuPanelKeydown.bind(this));
    this.cache.megamenu.addEventListener('load', () => this.syncExtras(), true);

    document.querySelectorAll(this.selectors.trigger).forEach(tr => {
      tr.addEventListener('mousedown', (e) => e.preventDefault());

      tr.addEventListener('click', (e) => {
        if (Marshall.Header.isTinyLayout()) {
          e.preventDefault();
          Marshall.ModalsAndDrawers.showModalOrDrawer('drawer-menu');
          return;
        }
        e.preventDefault();
        const parentHandle = tr.dataset.megamenuParent;
        const panel = this.cache.megamenu.querySelector(`${this.selectors.panel}[data-megamenu-panel="${parentHandle}"]`);
        if (!panel) return;
        const isAlreadyOpen = this.cache.megamenu.classList.contains('is-open') && panel.classList.contains('is-active');
        if (isAlreadyOpen) {
          this.close();
        } else {
          this.open(parentHandle);
        }
      });
    });

    this.cache.megamenu.querySelectorAll(this.selectors.close).forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    this.cache.megamenu.addEventListener('click', (e) => {
      if (!this.cache.megamenu.classList.contains('is-open')) return;
      if (e.target.closest('[data-megamenu-trigger]') || e.target.closest('[data-megamenu-hamburger]')) return;
      if (e.target.closest('#megamenu a[href]')) return;
      if (e.target.closest('#megamenu form')) return;
      e.preventDefault();
      this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.cache.megamenu && this.cache.megamenu.classList.contains('is-open')) {
        this.close();
      }
    });

    const onLayoutMaybeClose = throttle(() => {
      if (Marshall.Header.isTinyLayout()) this.close();
    }, 40);

    const onLayoutUpdateActivePanelAux = debounce(() => {
      this.syncExtras();
    }, 120);

    window.addEventListener('resize', onLayoutMaybeClose);
    window.addEventListener('resize', onLayoutUpdateActivePanelAux);
    if (Marshall.Header.isTinyLayout()) this.close();

    /* Mobile-only full-width drawers: close when switching to desktop */
    const onLayoutCloseFullWidthDrawers = throttle(() => {
      if (Marshall.Header.isTinyLayout()) return;
      ['drawer-menu', 'drawer-localization'].forEach((id) => {
        const el = document.getElementById(id);
        if (el?.classList.contains('is-open')) {
          Marshall.ModalsAndDrawers.closeModalOrDrawer(id);
        }
      });
    }, 40);
    window.addEventListener('resize', onLayoutCloseFullWidthDrawers);

    Marshall.Header.syncHeaderMenuToggleState();
  }
};

// -------
// Header search mode: search appears inside header, with page mask behind.
Marshall.HeaderSearchOverlay = {
  selectors: {
    overlay: '[data-search-overlay]',
    panel: '.header-search-overlay__panel',
    trigger: '[data-search-overlay-trigger]',
    close: '[data-search-overlay-close]',
    input: '#headerSearchOverlayInput'
  },
  cache: {
    initialized: false
  },

  /** Resolve and return the search overlay root element. */
  getOverlay: function () {
    return document.querySelector(this.selectors.overlay);
  },

  /** Sync aria-expanded on all search overlay trigger buttons. */
  setTriggerExpanded: function (expanded) {
    document.querySelectorAll(this.selectors.trigger).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  },

  /** Open header search and close other open overlays/drawers first. */
  open: function () {
    const overlay = this.getOverlay();
    if (!overlay) return;
    const headerGroup = overlay.closest('.site-header-group');
    const siteHeader = overlay.closest('.site-header');

    if (typeof Marshall.Megamenu?.close === 'function') {
      Marshall.Megamenu.close();
    }
    document.querySelectorAll('.drawer.is-open').forEach((drawer) => {
      if (drawer.id) {
        Marshall.ModalsAndDrawers.closeModalOrDrawer(drawer.id);
      }
    });
    document.querySelectorAll('.modal.is-open').forEach((modal) => {
      if (modal.id) {
        Marshall.ModalsAndDrawers.closeModalOrDrawer(modal.id);
      }
    });

    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    if (headerGroup) {
      headerGroup.classList.add('search-open');
    }
    if (siteHeader) {
      siteHeader.classList.add('search-open');
    }
    document.body.classList.add('overflow-hidden');
    this.setTriggerExpanded(true);
    Marshall.Media.pausePlayingMedia(document, { keepAnnouncementsBarPlaying: true });

    const searchInput = overlay.querySelector(this.selectors.input);
    if (searchInput) {
      requestAnimationFrame(() => searchInput.focus({ preventScroll: true }));
    }

    Marshall.Header.syncHeaderMenuToggleState();
  },

  /** Close header search and reset related UI/aria states. */
  close: function () {
    const overlay = this.getOverlay();
    if (!overlay || !overlay.classList.contains('is-open')) return;
    const headerGroup = overlay.closest('.site-header-group');
    const siteHeader = overlay.closest('.site-header');
    const predictiveSearch = overlay.querySelector('predictive-search');

    if (predictiveSearch && typeof predictiveSearch.close === 'function') {
      predictiveSearch.close(true);
    }

    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    if (headerGroup) {
      headerGroup.classList.remove('search-open');
    }
    if (siteHeader) {
      siteHeader.classList.remove('search-open');
    }
    this.setTriggerExpanded(false);
    document.body.classList.remove('overflow-hidden');
    Marshall.Media.resumePausedMedia();
    Marshall.Header.syncHeaderMenuToggleState();
  },

  /** Bind click/escape interactions for the header search overlay. */
  init: function () {
    if (this.cache.initialized) return;
    this.cache.initialized = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(this.selectors.trigger);
      if (trigger) {
        event.preventDefault();
        const overlay = this.getOverlay();
        if (!overlay) return;
        if (overlay.classList.contains('is-open')) {
          this.close();
        } else {
          this.open();
        }
        return;
      }

      const closeButton = event.target.closest(this.selectors.close);
      if (closeButton) {
        event.preventDefault();
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const overlay = this.getOverlay();
      if (!overlay || !overlay.classList.contains('is-open')) return;
      this.close();
    });
  }
};

// -------
// Marshall Common JS - Modals & drawers library
Marshall.ModalsAndDrawers = {
  settings: {
    selectors: {
      modalLink: 'a[href*="#modal"]:not(.quick-buy_link)',
      drawerLink: 'a[href*="#drawer"]',
    },
  },
  focusTraps: {},

  init: function () {
    // Init open event listeners
    this.bindTriggers(document);

    // Init close event listeners
    const closeBtns = document.querySelectorAll('[data-close]');
    if (closeBtns) {
      closeBtns.forEach((closeBtn) => {
        closeBtn.addEventListener('click', this.closeModalOrDrawerOrDrawerFromEvent.bind(this));
      });
    }

    const overlays = document.querySelectorAll('[data-overlay]');
    if (overlays) {
      overlays.forEach((overlay) => {
        overlay.addEventListener('click', this.closeModalOrDrawerOrDrawerFromEvent.bind(this));
      });
    }

    // Escape to close all
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        document.querySelectorAll('.drawer').forEach((drawer) => {
          Marshall.ModalsAndDrawers.emitDrawerState(drawer.id, false, 'escape');
          drawer.classList.remove('is-open');
        });
        document.querySelectorAll('.modal').forEach((modal) => {
          modal.classList.remove('is-open');
        });
        document.body.classList.remove('overflow-hidden');

        // Everything is closing; resume the single active snapshot (a no-op if none / excluded).
        Marshall.Media.resumePausedMedia();

        Marshall.Header.syncHeaderMenuToggleState();
      }
    });
  },
  /**
   * Binds modal and drawer triggers within a root element. Called again for
   * server-rendered markup that replaces existing triggers, e.g. the product
   * CTA after a variant change.
   * @param {Element|Document} root - Element to search for triggers.
   */
  bindTriggers: function () {
    if (this.openTriggersBound) {
      return;
    }

    this.openTriggersBound = true;

    document.addEventListener('click', (event) => {
      const modalLink = event.target.closest(this.settings.selectors.modalLink);
      if (modalLink) {
        event.preventDefault();
        const hrefHash = this.getHrefHash(modalLink);
        const targetId = hrefHash.split('?')[0];
        const slideIndex = hrefHash.split('?slide=')[1];
        const scrollToId = hrefHash.split('?scroll=')[1];
        this.showModalOrDrawer(targetId, parseInt(slideIndex, 10), scrollToId);
        return;
      }

      const drawerLink = event.target.closest(this.settings.selectors.drawerLink);
      if (drawerLink) {
        event.preventDefault();
        const targetId = this.getHrefHash(drawerLink).split('?')[0];
        this.showModalOrDrawer(targetId);
      }
    });
  },

  getHrefHash: function (link) {
    const href = link.getAttribute('href') || '';
    const hashIndex = href.indexOf('#');

    if (hashIndex === -1) {
      return '';
    }

    return href.substring(hashIndex + 1);
  },

  /**
   *
   * @param {string} targetId - ID of modal or drawer element, e.g. modal-geo, drawer-menu
   * @param {int} slideIndex - Index of a Gridy Slider slide inside the opened modal - (sliding motion)
   * @param {string} scrollToId - ID of a Gridy Slider slide inside the opened modal - (no motion)
   * Traps focus to modal and disables body scrolling.
   */
  showModalOrDrawer: function (targetId, slideIndex, scrollToId) {
    const targetElement = document.getElementById(targetId);
    if (!targetElement && Marshall.Utils.debugMode) {
      console.warn('No modal/drawer element specified');
    }
    if (!targetElement) {
      return;
    }
    if (targetId === 'drawer-menu' && !Marshall.Header.isTinyLayout()) {
      if (typeof Shopify === 'undefined' || !Shopify.designMode) {
        return;
      }
    }

    if (targetElement.classList.contains('is-open')) {
      if (document.body.classList.contains('overflow-hidden')) {
        return;
      }
      
      targetElement.classList.remove('is-open');
    }

    /* Close desktop megamenu so it never stacks with another drawer/modal */
    if (typeof Marshall.Megamenu?.close === 'function') {
      Marshall.Megamenu.close();
    }
    if (typeof Marshall.HeaderSearchOverlay?.close === 'function') {
      Marshall.HeaderSearchOverlay.close();
    }

    targetElement.classList.add('initialized', 'is-open');
    if (targetElement.classList.contains('drawer')) {
      this.emitDrawerState(targetId, true, 'showModalOrDrawer');
    }
    document.body.classList.add('overflow-hidden');

    // Keep announcements bar marquee running while the menu drawer is open.
    Marshall.Media.pausePlayingMedia(document, { keepAnnouncementsBarPlaying: targetId === 'drawer-menu' });

    setTimeout(() => {
      trapFocus(targetElement);

      // Focus on drawerSearchInput if it exists
      const drawerSearchInput = targetElement.querySelector('#drawerSearchInput');
      if (drawerSearchInput) {
        drawerSearchInput.focus();
      }
    }, 600);

    if (slideIndex) {
      setTimeout(() => {
        const emblaRoot = targetElement.querySelector('.embla[data-embla], embla-carousel[data-embla]');
        if (emblaRoot && emblaRoot.emblaApi) {
          emblaRoot.emblaApi.scrollTo(slideIndex - 1, true);
        }
      }, 600);
    } else if (scrollToId) {
      const scrollToElement = document.getElementById(scrollToId);
      const emblaRoot = targetElement.querySelector('.embla[data-embla], embla-carousel[data-embla]');
      if (emblaRoot && emblaRoot.emblaApi && scrollToElement) {
        const viewport = emblaRoot.querySelector('.embla__viewport');
        const slides = viewport && viewport.querySelectorAll('.embla__slide');
        const slideEl = scrollToElement.closest ? scrollToElement.closest('.embla__slide') : scrollToElement;
        const idx = slides ? Array.from(slides).indexOf(slideEl) : -1;
        if (idx >= 0) emblaRoot.emblaApi.scrollTo(idx, true);
      } else if (scrollToElement) {
        const modal_dialog = targetElement.querySelector('.embla__viewport');
        if (modal_dialog) {
          modal_dialog.scrollTo({
            top: scrollToElement.offsetTop,
            left: scrollToElement.offsetLeft,
            behavior: "instant"
          });
        }
      }
    }

    Marshall.Header.syncHeaderMenuToggleState();

  },
  closeModalOrDrawer: function (targetId) {
    const targetElement = document.getElementById(targetId);
    if (!targetElement) {
      return;
    }
    removeTrapFocus();
    if (targetElement.classList.contains('drawer')) {
      this.emitDrawerState(targetId, false, 'closeModalOrDrawer');
    }
    targetElement.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');

    // Resume the media that was paused when this overlay opened.
    Marshall.Media.resumePausedMedia();

    Marshall.Header.syncHeaderMenuToggleState();
  },
  closeModalOrDrawerOrDrawerFromEvent: function (event) {
    event.preventDefault();
    removeTrapFocus();
    const parentElement = event.currentTarget.closest('[data-parent]');
    if (parentElement?.classList.contains('drawer')) {
      this.emitDrawerState(parentElement.id, false, 'closeFromEvent');
    }
    parentElement.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');

    // Resume the media that was paused when this overlay opened.
    Marshall.Media.resumePausedMedia();

    Marshall.Header.syncHeaderMenuToggleState();
  },
  emitDrawerState: function (targetId, isOpen, source) {
    document.dispatchEvent(new CustomEvent('marshall:drawer-state', {
      detail: {
        targetId,
        isOpen,
        source: source || 'unknown'
      }
    }));
  },
};

/**
 * Allow us to trigger something when window is actually resized.
 */
let marshall_window_size = window.innerWidth;
window.addEventListener(
  'resize',
  debounce(() => {
    if (window.innerWidth == marshall_window_size) {
      return;
    }
    marshall_window_size = window.innerWidth;
    document.dispatchEvent(new CustomEvent('marshall:resize'));
  }, 250)
);

/**
 * Animated details element
 */
class Accordion {
  static animationDuration = 250;
  static closeDuration = 120;
  static instances = new WeakMap();

  constructor(el) {
    if (el.hasAttribute('data-no-accordion-logic')) {
      return;
    }

    this.el = el;
    this.summary = el.querySelector('summary');
    this.content = el.querySelector('.content');
    this.video = this.content ? this.content.querySelector('video') : null;

    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.openAfterCloseTimeout = null;

    Accordion.instances.set(this.el, this);

    this.summary.addEventListener('click', (e) => this.onClick(e));
    this.toggleByViewport();
    document.addEventListener('marshall:resize', this.toggleByViewport.bind(this));
  }

  getGroupedOpenSiblings() {
    const collapsibleGroup = this.el.dataset.collapsibleGroup;
    if (collapsibleGroup == null || collapsibleGroup === '') {
      return [];
    }

    const scope = this.el.closest('[data-collapsible-group-scope]') || this.el.closest('.shopify-section') || document;
    const groupedSelector = `details[data-collapsible-group="${collapsibleGroup}"][open]`;
    return [...scope.querySelectorAll(groupedSelector)].filter((detailsElement) => detailsElement !== this.el);
  }

  onClick(e) {
    e.preventDefault();
    clearTimeout(this.openAfterCloseTimeout);
    this.el.style.overflow = 'hidden';

    if (this.isClosing || !this.el.open) {
      const groupedOpenSiblings = this.getGroupedOpenSiblings();

      if (groupedOpenSiblings.length === 0) {
        this.open();
        return;
      }

      groupedOpenSiblings.forEach((detailsElement) => {
        const siblingAccordion = Accordion.instances.get(detailsElement);

        if (siblingAccordion) {
          siblingAccordion.shrink();
        } else {
          detailsElement.removeAttribute('open');
        }
      });

      this.openAfterCloseTimeout = setTimeout(() => {
        if (!this.el.open && !this.isExpanding) {
          this.open();
        }
      }, Accordion.closeDuration);
    } else if (this.isExpanding || this.el.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;
    this.el.style.overflow = 'hidden';

    // Pause video if it exists and is playing
    if (this.video && !this.video.paused) {
      this.video.pause();
    }

    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.el.animate({
      height: [startHeight, endHeight]
    }, {
      duration: Accordion.closeDuration,
      easing: 'ease-in-out'
    });

    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => this.isClosing = false;
  }

  open() {
    this.el.style.height = `${this.el.offsetHeight}px`;
    this.el.open = true;
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;
    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.el.animate({
      height: [startHeight, endHeight]
    }, {
      duration: Accordion.animationDuration,
      easing: 'ease-out'
    });
    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => this.isExpanding = false;
  }

  onAnimationFinish(open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.el.style.height = this.el.style.overflow = '';

    // If accordion is closed and video exists, ensure it's paused
    if (!open && this.video && !this.video.paused) {
      this.video.pause();
    }
  }

  toggleByViewport() {
    let mobileClose = this.el.dataset.mobileClose != undefined ? JSON.parse(this.el.dataset.mobileClose) : false;
    let desktopOpen = this.el.dataset.desktopOpen != undefined ? JSON.parse(this.el.dataset.desktopOpen) : false;

    if (marshall_window_size >= 768 && desktopOpen) {
      this.el.open = true;
    } else if (mobileClose) {
      this.el.open = false;
      // Pause video when accordion is closed by viewport toggle
      if (this.video && !this.video.paused) {
        this.video.pause();
      }
    }
  }
};

Marshall.DetailsAccordions = {
  init: function () {
    document.querySelectorAll('details').forEach((el) => {
      new Accordion(el);
    });
  }
};

// -------
// Marshall Common JS - Media pause/resume
// Per-item primitives plus two consumers: the temporary overlay snapshot (pausePlayingMedia)
// and the persistent reduced-motion pausing (pauseForReducedMotion).
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

Marshall.Media = {
  // Snapshot of what was playing when an overlay/menu opened, so only that set is resumed.
  menuPauseSnapshot: null,

  // ---- Native <video> ----
  isVideoPlaying: function (video) {
    return !video.paused && !video.ended && video.readyState > 2;
  },

  pauseVideo: function (video) {
    if (!video.paused) video.pause();
  },

  playVideo: function (video) {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
  },

  // De-autoplay and pause an autoplaying <video> (reduced motion keeps it paused).
  deautoplayVideo: function (video) {
    if (video.dataset.reducedMotionPaused) return;

    video.dataset.reducedMotionPaused = 'true';
    video.autoplay = false;
    video.removeAttribute('autoplay');

    const pauseVideo = () => {
      if (!video.paused) video.pause();
    };

    pauseVideo();

    // The browser may still kick off playback right after load, so pause once more.
    video.addEventListener('loadeddata', pauseVideo, { once: true });
  },

  // ---- Native <audio> ----
  isAudioPlaying: function (audio) {
    return !audio.paused && !audio.ended && audio.readyState > 2;
  },

  pauseAudio: function (audio) {
    if (!audio.paused) audio.pause();
  },

  playAudio: function (audio) {
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
  },

  // Reload an autoplaying YouTube / Vimeo embed without autoplay (autoplay=0 / background=0).
  neutralizeAutoplayEmbed: function (iframe) {
    if (iframe.dataset.reducedMotionPaused) return;

    const currentSrc = iframe.getAttribute('src') || '';
    const isSupportedHost = /youtube|youtu\.be|vimeo/.test(currentSrc);
    const isAutoplaying = /autoplay=1|background=1/i.test(currentSrc);

    if (!isSupportedHost || !isAutoplaying) return;

    const pausedSrc = currentSrc
      .replace(/autoplay=1/gi, 'autoplay=0')
      .replace(/background=1/gi, 'background=0');

    iframe.dataset.reducedMotionPaused = 'true';
    iframe.setAttribute('src', pausedSrc);
  },

  // ---- Embedded players (YouTube / Vimeo <iframe>) ----
  // Cross-origin play state can't be read, so the overlay path only touches autoplaying embeds
  // and only resumes the exact iframes it paused (tracked via dataset.overlayPaused).
  isAutoplayEmbed: function (iframe) {
    const src = iframe.getAttribute('src') || '';
    if (!validateVideoUrl(src)) return false;

    return /autoplay=1|background=1/i.test(src);
  },

  pauseEmbed: function (iframe) {
    const provider = validateVideoUrl(iframe.getAttribute('src') || '');
    if (!provider || !iframe.contentWindow) return;

    if (provider.type === 'youtube') {
      iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    } else {
      iframe.contentWindow.postMessage('{"method":"pause"}', '*');
    }
  },

  playEmbed: function (iframe) {
    const provider = validateVideoUrl(iframe.getAttribute('src') || '');
    if (!provider || !iframe.contentWindow) return;

    if (provider.type === 'youtube') {
      iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else {
      iframe.contentWindow.postMessage('{"method":"play"}', '*');
    }
  },

  // ---- Carousels (Embla autoplay / auto-scroll) ----
  // The plugin active at the current breakpoint (see activeAutoPlugin in embla-carousel-init.js).
  getCarouselAutoPlugin: function (carousel) {
    return (carousel && carousel.activeAutoPlugin && carousel.activeAutoPlugin()) || null;
  },

  isCarouselAutoPlaying: function (carousel) {
    const found = this.getCarouselAutoPlugin(carousel);
    return !!(found && typeof found.plugin.isPlaying === 'function' && found.plugin.isPlaying());
  },

  isCarouselPausedByOverlay: function (carousel) {
    const snapshot = this.menuPauseSnapshot;

    return Boolean(snapshot && snapshot.carousels.includes(carousel));
  },

  stopCarousel: function (carousel) {
    const found = this.getCarouselAutoPlugin(carousel);
    if (found && typeof found.plugin.stop === 'function') found.plugin.stop();
  },

  playCarousel: function (carousel) {
    const found = this.getCarouselAutoPlugin(carousel);
    if (!found || typeof found.plugin.play !== 'function') return;

    if (found.type === 'autoScroll') {
      found.plugin.play(0);
      return;
    }

    found.plugin.play();
  },

  // ---- Animated GIFs ----
  isAnimatedGif: function (source) {
    return /\.gif(\?|#|$)/i.test(source);
  },

  // Per-media-type toggle config. syncOnMediaEvents mirrors the toggle to a native element's
  // own play/pause events (GIFs emit none).
  mediaToggleConfig: {
    gif: {
      containerSelector: '.media',
      isPaused: (image) => !!image.dataset.mediaFrozen,
      toggle: (image) => Marshall.Media.toggleGif(image),
      playLabel: (strings) => strings.playAnimation || 'Play animation',
      pauseLabel: (strings) => strings.pauseAnimation || 'Pause animation'
    },
    video: {
      containerSelector: '.content-section__media',
      isPaused: (video) => video.paused,
      toggle: (video) => Marshall.Media.toggleVideo(video),
      playLabel: (strings) => strings.playVideo || 'Play video',
      pauseLabel: (strings) => strings.pauseVideo || 'Pause video',
      syncOnMediaEvents: true
    }
  },

  findMediaToggle: function (media, config) {
    const container = media.closest(config.containerSelector) || media.parentElement;
    return (container && container.querySelector('.media-play-toggle')) || null;
  },

  syncMediaToggle: function (media, config) {
    const toggle = this.findMediaToggle(media, config);
    if (!toggle) return;

    const paused = config.isPaused(media);
    const strings = window.accessibilityStrings || {};

    toggle.innerHTML = mediaControlIcon(paused ? 'play' : 'pause');
    toggle.setAttribute('aria-label', paused ? config.playLabel(strings) : config.pauseLabel(strings));
  },

  // Always-visible play/pause toggle for a media element (GIFs and controls-less autoplay videos).
  addMediaToggle: function (media, config) {
    const container = media.closest(config.containerSelector) || media.parentElement;
    if (!container || container.querySelector('.media-play-toggle')) return;

    const onToggle = (event) => {
      // Media may sit inside a link; don't navigate, and don't double-fire the handlers below.
      event.preventDefault();
      event.stopPropagation();
      config.toggle(media);
    };

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'media-play-toggle';
    toggle.addEventListener('click', onToggle);

    container.appendChild(toggle);

    // Clicking the media itself also toggles it, unless it's wrapped in a link (keep navigation).
    if (!media.closest('a')) {
      media.classList.add('gif-clickable');
      media.addEventListener('click', onToggle);
    }

    // Native <video> emits play/pause, so mirror its real state (GIFs don't; their play/freeze
    // helpers re-sync instead).
    if (config.syncOnMediaEvents) {
      media.addEventListener('play', () => this.syncMediaToggle(media, config));
      media.addEventListener('pause', () => this.syncMediaToggle(media, config));
    }

    this.syncMediaToggle(media, config);
  },

  // Keep a GIF's toggle in sync; called by the GIF play/freeze helpers.
  syncGifToggle: function (image) {
    this.syncMediaToggle(image, this.mediaToggleConfig.gif);
  },

  // Add the GIF toggle to every animated GIF in scope. Frozen GIFs (already swapped to a still)
  // are matched via their stored source so the control is still offered under reduced motion.
  initGifToggles: function (root) {
    const scope = root || document;

    scope.querySelectorAll('img').forEach((image) => {
      // Skip marquees: their items are duplicated (and aria-hidden), so a per-item
      // toggle would land on every copy and sit inside decorative content.
      if (image.closest('.sliding-text, .hero-with-scrolling-text__marquee')) return;
      // Opt-out for decorative GIFs that shouldn't get a control.
      if (image.hasAttribute('data-no-media-toggle')) return;

      const source = image.dataset.mediaGifSrc || image.currentSrc || image.src || '';

      if (this.isAnimatedGif(source) || image.dataset.mediaFrozen) {
        this.addMediaToggle(image, this.mediaToggleConfig.gif);
      }
    });
  },

  // Add the toggle to every controls-less autoplay video in scope (e.g. content section).
  initVideoToggles: function (root) {
    const scope = root || document;

    scope.querySelectorAll('.content-section__media video').forEach((video) => {
      this.addMediaToggle(video, this.mediaToggleConfig.video);
    });
  },

  // User-driven toggle: pause a playing video, or play a paused one.
  toggleVideo: function (video) {
    if (video.paused) {
      this.playVideo(video);
    } else {
      this.pauseVideo(video);
    }
  },

  // User-driven toggle: restore an animating GIF's still, or freeze an animating one.
  toggleGif: function (image) {
    if (image.dataset.mediaFrozen) {
      this.playGif(image);
      return;
    }

    // Freezing can fail if the CDN response is not CORS-enabled; the GIF then keeps animating.
    this.freezeGif(image);
  },

  // Restore a GIF frozen by freezeGif and re-sync its toggle (shared by the toggle and resume).
  playGif: function (image) {
    const animatedSrc = image.dataset.mediaGifSrc;
    if (!animatedSrc) return;

    if (image.dataset.mediaGifSrcset) {
      image.setAttribute('srcset', image.dataset.mediaGifSrcset);
      delete image.dataset.mediaGifSrcset;
    }

    image.src = animatedSrc;

    delete image.dataset.mediaFrozen;
    delete image.dataset.mediaGifSrc;

    this.syncGifToggle(image);
  },

  freezeGif: function (image) {
    if (image.dataset.mediaFrozen) return;

    const animatedSrc = image.currentSrc || image.src;
    if (!animatedSrc) return;

    // Draw the first frame onto a canvas via a CORS-enabled request so the still can be exported.
    const capture = new Image();
    capture.crossOrigin = 'anonymous';

    capture.addEventListener('load', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = capture.naturalWidth;
        canvas.height = capture.naturalHeight;
        canvas.getContext('2d').drawImage(capture, 0, 0);

        const stillFrame = canvas.toDataURL('image/png');

        image.dataset.mediaGifSrc = animatedSrc;

        if (image.getAttribute('srcset')) {
          image.dataset.mediaGifSrcset = image.getAttribute('srcset');
          image.removeAttribute('srcset');
        }

        image.src = stillFrame;
        image.dataset.mediaFrozen = 'true';

        this.syncGifToggle(image);
      } catch (error) {
        // Export can fail if the CDN response is not CORS-enabled; leave the GIF untouched.
      }
    });

    capture.src = animatedSrc;
  },

  // ---- 3D models (<model-viewer>) ----
  isModelViewerPlaying: function (viewer) {
    // `paused` is only defined once the custom element has upgraded and has an animation.
    return viewer.paused === false;
  },

  pauseModelViewer: function (viewer) {
    if (typeof viewer.pause === 'function') viewer.pause();
  },

  playModelViewer: function (viewer) {
    if (typeof viewer.play === 'function') viewer.play();
  },

  // Pause everything playing and snapshot it so an overlay can resume exactly that set on close.
  // Already paused/frozen items never enter the snapshot, so user-paused media stays paused.
  // Overlays that keep the announcements bar on screen pass keepAnnouncementsBarPlaying.
  pausePlayingMedia: function (root, options) {
    if (this.menuPauseSnapshot) return;

    const scope = root || document;
    const keepAnnouncementsBarPlaying = Boolean(options && options.keepAnnouncementsBarPlaying);
    const snapshot = { videos: [], audios: [], carousels: [], gifs: [], embeds: [], modelViewers: [] };

    scope.querySelectorAll('video').forEach((video) => {
      if (!this.isVideoPlaying(video)) return;

      snapshot.videos.push(video);
      this.pauseVideo(video);
    });

    scope.querySelectorAll('audio').forEach((audio) => {
      if (!this.isAudioPlaying(audio)) return;

      snapshot.audios.push(audio);
      this.pauseAudio(audio);
    });

    scope.querySelectorAll('embla-carousel').forEach((carousel) => {
      if (keepAnnouncementsBarPlaying && carousel.closest('.announcements-bar')) return;
      if (!this.isCarouselAutoPlaying(carousel)) return;

      snapshot.carousels.push(carousel);
      this.stopCarousel(carousel);
    });

    scope.querySelectorAll('img').forEach((image) => {
      if (image.dataset.mediaFrozen) return;

      const source = image.currentSrc || image.src || '';
      if (!this.isAnimatedGif(source)) return;

      snapshot.gifs.push(image);
      this.freezeGif(image);
    });

    scope.querySelectorAll('iframe').forEach((iframe) => {
      if (iframe.dataset.reducedMotionPaused || iframe.dataset.overlayPaused) return;

      const provider = validateVideoUrl(iframe.getAttribute('src') || '');
      if (!provider) return;

      if (this.isAutoplayEmbed(iframe) || iframe.dataset.userPlaying === 'true') {
        iframe.dataset.overlayPaused = 'true';
        snapshot.embeds.push(iframe);
        this.pauseEmbed(iframe);
      } else {
        this.pauseEmbed(iframe);
      }
    });

    scope.querySelectorAll('model-viewer').forEach((viewer) => {
      if (!this.isModelViewerPlaying(viewer)) return;

      snapshot.modelViewers.push(viewer);
      this.pauseModelViewer(viewer);
    });

    this.menuPauseSnapshot = snapshot;
  },

  resumePausedMedia: function () {
    const snapshot = this.menuPauseSnapshot;
    if (!snapshot) return;

    this.menuPauseSnapshot = null;

    // Audio is not motion, so resume it even if reduced motion is now preferred.
    snapshot.audios.forEach((audio) => this.playAudio(audio));

    // Motion media stays stopped if reduced motion became preferred while the overlay was open,
    // but the overlay pause flag must still be cleared so a later open can pause again.
    if (reducedMotionQuery.matches) {
      snapshot.embeds.forEach((iframe) => delete iframe.dataset.overlayPaused);
      return;
    }

    snapshot.videos.forEach((video) => this.playVideo(video));
    snapshot.carousels.forEach((carousel) => this.playCarousel(carousel));
    snapshot.gifs.forEach((image) => this.playGif(image));
    snapshot.embeds.forEach((iframe) => {
      this.playEmbed(iframe);
      delete iframe.dataset.overlayPaused;
    });
    snapshot.modelViewers.forEach((viewer) => this.playModelViewer(viewer));
  },

  // ---- Reduced motion (persistent) ----
  // Pause all media at load for reduced-motion visitors: de-autoplay videos, neutralize autoplay
  // embeds, freeze GIFs, and stop auto-advancing carousels (manual navigation still works).
  pauseAutoplayVideos: function (root) {
    root.querySelectorAll('video[autoplay]').forEach((video) => this.deautoplayVideo(video));
  },

  pauseAutoplayEmbeds: function (root) {
    root.querySelectorAll('iframe').forEach((iframe) => this.neutralizeAutoplayEmbed(iframe));
  },

  freezeGifs: function (root) {
    root.querySelectorAll('img').forEach((image) => {
      if (image.dataset.mediaFrozen) return;

      const source = image.currentSrc || image.src || '';
      if (this.isAnimatedGif(source)) this.freezeGif(image);
    });
  },

  stopCarousels: function (root) {
    root.querySelectorAll('embla-carousel').forEach((carousel) => this.stopCarousel(carousel));
  },

  // Persistently pause all motion media in scope for reduced-motion visitors (distinct from the
  // temporary overlay snapshot taken by pausePlayingMedia).
  pauseForReducedMotion: function (root) {
    const scope = root || document;

    this.pauseAutoplayVideos(scope);
    this.pauseAutoplayEmbeds(scope);
    this.freezeGifs(scope);
    this.stopCarousels(scope);
  },

  initReducedMotion: function () {
    if (reducedMotionQuery.matches) this.pauseForReducedMotion(document);

    // Re-run for sections re-rendered by the theme editor.
    document.addEventListener('shopify:section:load', (event) => {
      if (reducedMotionQuery.matches) this.pauseForReducedMotion(event.target);
    });

    // Apply immediately if the visitor turns on reduced motion during the session.
    reducedMotionQuery.addEventListener('change', (event) => {
      if (event.matches) this.pauseForReducedMotion(document);
    });
  }
};

// Tables inside a `.use__stacked-tables` container are restacked below 1024px by
// critical.css: each <tbody> row becomes a card and each cell a labelled row.
// The labels have to come from here — table markup authored in the Shopify admin
// (page.content, article.content, metafields) carries no per-cell heading, and
// CSS cannot read another element's text, so each column heading is copied onto
// its cells as `data-label`. The CSS only applies once [data-stacked-table] is
// set, so an untouched table still renders as a plain table.
Marshall.StackedTables = {
  rootSelector: '.use__stacked-tables',

  // Changing `display` on table elements drops their native semantics, so the
  // roles are restated explicitly.
  setRole: function (element, role) {
    if (element && !element.hasAttribute('role')) element.setAttribute('role', role);
  },

  headerRow: function (table) {
    if (table.tHead && table.tHead.rows.length) {
      return table.tHead.rows[table.tHead.rows.length - 1];
    }

    const firstRow = table.rows[0];
    if (!firstRow || !firstRow.cells.length) return null;

    const allHeadings = Array.from(firstRow.cells).every((cell) => cell.tagName === 'TH');
    return allHeadings ? firstRow : null;
  },

  labelRows: function (rows, labels) {
    rows.forEach((row) => {
      this.setRole(row, 'row');

      Array.from(row.cells).forEach((cell, index) => {
        this.setRole(cell, cell.tagName === 'TH' ? 'rowheader' : 'cell');

        const label = labels[index];
        // Cells spanning columns have no single heading — they stay unlabelled.
        if (!label || cell.colSpan !== 1 || cell.hasAttribute('data-label')) return;

        cell.setAttribute('data-label', label);
      });
    });
  },

  prepare: function (table) {
    if ('stackedTable' in table.dataset) return;

    const headerRow = this.headerRow(table);
    if (!headerRow || headerRow.cells.length < 2) return;

    const labels = Array.from(headerRow.cells).map((cell) => cell.textContent.trim());

    this.setRole(table, 'table');
    this.setRole(headerRow.parentElement, 'rowgroup');
    this.setRole(headerRow, 'row');
    Array.from(headerRow.cells).forEach((cell) => this.setRole(cell, 'columnheader'));

    Array.from(table.tBodies).forEach((body) => {
      this.setRole(body, 'rowgroup');
      this.labelRows(Array.from(body.rows), labels);
    });

    if (table.tFoot) {
      this.setRole(table.tFoot, 'rowgroup');
      this.labelRows(Array.from(table.tFoot.rows), []);
    }

    table.dataset.stackedTable = '';
  },

  init: function (root) {
    const scope = root || document;
    if (typeof scope.querySelectorAll !== 'function') return;

    // The opt-in element may be the scope itself (e.g. a re-rendered section).
    if (typeof scope.matches === 'function' && scope.matches(this.rootSelector)) {
      scope.querySelectorAll('table').forEach((table) => this.prepare(table));
    }

    scope.querySelectorAll(`${this.rootSelector} table`).forEach((table) => this.prepare(table));
  }
};

Marshall.ProductCards = {
  hoverImagesBound: new WeakSet(),

  init: function () {
    const sync = debounce(() => this.sync(document), 80);

    this.sync(document);
    this.bindHoverImages(document);
    requestAnimationFrame(() => this.sync(document));

    window.addEventListener('resize', sync);
    window.addEventListener('load', sync);
    document.addEventListener('theme:layout-changed', sync);
    document.fonts?.ready.then(sync);

    new MutationObserver((mutations) => {
      const addedCard = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) =>
          node.nodeType === 1 && (node.matches?.('.card--product') || node.querySelector?.('.card--product'))
        )
      );

      if (addedCard) {
        sync();
        this.bindHoverImages(document);
      }
    }).observe(document.body, { childList: true, subtree: true });
  },

  priceWraps: function (card) {
    const title = card.querySelector('.card-title');
    const price = card.querySelector('.price');

    if (!title || !price || !title.offsetWidth || !price.offsetWidth) return false;

    return price.offsetTop >= title.offsetTop + title.offsetHeight / 2
      && price.offsetLeft < title.offsetLeft + title.offsetWidth;
  },

  sync: function (root) {
    const scope = root && root.querySelectorAll ? root : document;
    const cards = [...scope.querySelectorAll('.card--product')];

    if (scope.matches?.('.card--product')) cards.push(scope);

    const groups = new Set(cards.flatMap((card) => {
      if (card.closest('.predictive-search__results-list--products')) return [];

      return [card.closest('.embla__container, #product-grid-ajax') || card.parentElement];
    }).filter(Boolean));

    groups.forEach((group) => {
      const groupCards = [...group.querySelectorAll('.card--product')];
      const rows = [];

      groupCards.forEach((card) => {
        card.classList.remove('card--price-stacked');
        card.style.removeProperty('--card-title-min-height');
      });
      void group.offsetWidth;

      groupCards.forEach((card) => {
        const top = Math.round(card.getBoundingClientRect().top);
        const row = rows.find((entry) => Math.abs(entry.top - top) <= 2);

        if (row) row.cards.push(card);
        else rows.push({ top, cards: [card] });
      });

      rows.forEach((row) => {
        if (row.cards.length < 2 || !row.cards.some((card) => this.priceWraps(card))) return;

        row.cards.forEach((card) => card.classList.add('card--price-stacked'));
        void group.offsetWidth;

        const titleHeight = Math.max(...row.cards.map((card) => card.querySelector('.card-title')?.offsetHeight || 0));

        row.cards.forEach((card) => {
          card.style.setProperty('--card-title-min-height', `${titleHeight}px`);
        });
      });
    });
  },

  bindHoverImages: function (root) {
    const scope = root && root.querySelectorAll ? root : document;

    scope.querySelectorAll('.card.on-hover[data-hover-image-loaded="false"]').forEach((card) => {
      this.bindHoverImage(card);
    });
  },

  bindHoverImage: function (card) {
    if (this.hoverImagesBound.has(card)) return;

    const hoverImage = card.querySelector('.card-hover-images > *:last-child img');

    if (!hoverImage) return;

    this.hoverImagesBound.add(card);
    this.markHoverImageWhenReady(card, hoverImage);
  },

  markHoverImageWhenReady: async function (card, hoverImage) {
    const isPaintReady = () => hoverImage.complete && hoverImage.naturalWidth > 0;
    const isFailed = hoverImage.complete && hoverImage.naturalWidth === 0 && Boolean(hoverImage.currentSrc);

    if (isFailed) return;

    try {
      if (!isPaintReady()) {
        await new Promise((resolve, reject) => {
          hoverImage.addEventListener('load', resolve, { once: true });
          hoverImage.addEventListener('error', reject, { once: true });
        });
      }

      if (typeof hoverImage.decode === 'function') {
        await hoverImage.decode();
      }

      if (hoverImage.naturalWidth > 0) {
        card.dataset.hoverImageLoaded = 'true';
      }
    } catch {
      // Keep hover swap disabled if the image fails to load or decode.
    }
  }
};

// Applies ?discount=CODE to the cart and updates the cart UI.
Marshall.Discounts = {
  ready: Promise.resolve(),

  init: function () {
    const params = new URLSearchParams(window.location.search);
    const discountCode = params.get('discount');

    if (!discountCode) {
      this.ready = Promise.resolve();
      return;
    }

    this.ready = this.applyDiscount(discountCode)
      .then(() => this.fetchCart())
      .then((cart) => {
        if (this.isDiscountApplied(cart, discountCode)) {
          // An empty cart still holds the code (cookie + cart.discount_codes), but
          // opening the drawer would only show a blank cart. The event is what
          // <discounted-price> needs so cards pick the code up.
          if (cart.item_count > 0) {
            this.refreshAndOpenCart();
          }

          document.dispatchEvent(new CustomEvent('marshall:discount-applied', {
            detail: { code: discountCode },
          }));
        } else {
          console.warn(`Discount "${discountCode}" was not applied to the cart.`);

          // Invalid/non-applicable code: still open the cart and show the same inline
          // error a manually entered bad code shows, instead of failing silently.
          this.openCartWithDiscountError();

          document.dispatchEvent(new CustomEvent('marshall:discount-not-applied', {
            detail: { code: discountCode },
          }));
        }
      })
      .catch((error) => {
        // Leave the cart untouched (and closed) if the code could not be applied
        console.error('Discount could not be applied:', error);
      })
      .finally(() => {
        this.removeDiscountFromUrl();
      });
  },
  /**
   * Hits Shopify's /discount/<code> endpoint so it sets the real discount cookie
   * same-origin, without a full-page redirect.
   */
  applyDiscount: async function (discountCode) {
    const rootUrl = window.routes.root === '/' ? '/' : window.routes.root + '/';
    const response = await fetch(`${rootUrl}discount/${encodeURIComponent(discountCode)}`);

    if (!response.ok) {
      throw new Error(`Failed to apply discount (${response.status})`);
    }
  },
  /**
   * Reads the current cart as JSON so the applied discount can be verified.
   */
  fetchCart: async function () {
    const rootUrl = window.routes.root === '/' ? '/' : window.routes.root + '/';
    const cartUrl = window.routes.cart_url || `${rootUrl}cart`;
    const response = await fetch(`${cartUrl}.js`);

    if (!response.ok) {
      throw new Error(`Failed to read cart (${response.status})`);
    }

    return response.json();
  },
  /**
   * Verifies the code is on the cart (case-insensitive, since Shopify normalizes
   * case). Applications only appear on line items, so an empty cart is checked
   * through cart.discount_codes — that list is what /discount/<code> writes.
   */
  isDiscountApplied: function (cart, discountCode) {
    if (!cart) {
      return false;
    }

    const code = discountCode.toLowerCase();
    const matches = (value) => typeof value === 'string' && value.toLowerCase() === code;

    const codeListed = (cart.discount_codes || []).some((entry) => matches(entry.code));

    const cartLevelApplied = (cart.cart_level_discount_applications || []).some(
      (discount) => matches(discount.title) || matches(discount.code)
    );

    const itemLevelApplied = (cart.items || []).some((item) =>
      (item.discounts || []).some((discount) => matches(discount.title) || matches(discount.code))
    );

    return codeListed || cartLevelApplied || itemLevelApplied;
  },
  /**
   * Re-renders the cart and opens the drawer via the same routine used after
   * add-to-cart (Section Rendering API + Marshall.ModalsAndDrawers), so the
   * applied discount and updated totals are shown.
   */
  refreshAndOpenCart: async function () {
    const cartItems = document.querySelector('cart-items');

    if (cartItems) {
      await customElements.whenDefined('cart-items');
      cartItems.renderAndShowItems();
    } else {
      Marshall.ModalsAndDrawers.showModalOrDrawer('drawer-cart');
    }
  },
  /**
   * Opens the cart drawer and surfaces the invalid-code error using the same inline
   * UI a manually entered bad code shows (the <cart-discount> gets `cart-discount--error`
   * and reveals its `[data-discount-error]` message). The cart isn't re-rendered because
   * an invalid code doesn't change it. If a code is already applied the input form isn't
   * rendered, so the cart still opens but no inline error is shown.
   */
  openCartWithDiscountError: function () {
    const cartItems = document.querySelector('cart-items');
    const drawer = cartItems && cartItems.closest('[data-drawer]');

    if (drawer) {
      Marshall.ModalsAndDrawers.showModalOrDrawer(drawer.getAttribute('id'));
    }

    const cartDiscount = document.getElementById('cart-discount');

    if (cartDiscount && cartDiscount.querySelector('[data-discount-input]') && typeof cartDiscount.showError === 'function') {
      cartDiscount.showError();
    }
  },
  removeDiscountFromUrl: function () {
    const params = new URLSearchParams(window.location.search);
    params.delete('discount');

    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  },
};

// Recalculates discounts on every page load, then redraws the cart. Shopify only
// re-evaluates automatic vs code discounts on a cart write, so the Liquid cart can
// keep showing a losing code until something is added or removed.
Marshall.Cart = {
  init: function () {
    const refresh = () => this.refresh();

    if (Marshall.Discounts?.ready) {
      Marshall.Discounts.ready.finally(refresh);
      return;
    }

    refresh();
  },
  refresh: async function () {
    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ updates: {} }),
      });

      if (!response.ok) return;

      const cart = await response.json();

      document.dispatchEvent(new CustomEvent('cart:update', { detail: cart }));

      const cartItems = document.querySelector('cart-items');

      if (!cartItems) return;

      await customElements.whenDefined('cart-items');
      await cartItems.renderItems();
    } catch (error) {
      console.warn('Marshall.Cart: unable to refresh the cart', error);
    }
  },
};

Marshall.init();

class LocalizationForm extends HTMLElement {
  #bound = false;
  #onItemClick = null;

  connectedCallback() {
    if (this.#bound) {
      return;
    }
    this.#bound = true;
    this.#onItemClick = this.#handleItemClick.bind(this);
    this.querySelectorAll('a.locale-row').forEach((item) => {
      item.addEventListener('click', this.#onItemClick);
    });
  }

  disconnectedCallback() {
    if (!this.#bound) {
      return;
    }
    this.querySelectorAll('a.locale-row').forEach((item) => {
      item.removeEventListener('click', this.#onItemClick);
    });
    this.#bound = false;
    this.#onItemClick = null;
  }

  #handleItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    if (!form) {
      return;
    }

    const link = event.currentTarget;
    const localeInput = form.querySelector('input[name="locale_code"]');
    const countryInput = form.querySelector('input[name="country_code"]');

    if (link.dataset.localeCode && localeInput) {
      localeInput.value = link.dataset.localeCode;
    }
    if (link.dataset.countryCode && countryInput) {
      countryInput.value = link.dataset.countryCode;
    }

    const returnField = form.querySelector('[name="return_to"]');
    if (returnField) {
      let returnToValue = returnField.value;
      const indexOfFilters = returnToValue.indexOf('?filter');
      if (indexOfFilters !== -1) {
        const substringAfterFilters = returnToValue.substring(indexOfFilters);
        const replacedSubstring = substringAfterFilters.replace(/%2C/g, ',');
        returnField.value = returnToValue.substring(0, indexOfFilters) + replacedSubstring;
      }
    }

    form.submit();
  }
}

customElements.define('localization-form', LocalizationForm);

// Custom element that enhances HTML Video Element functionality.
// Usage: <video is="superpowered-video">
class SuperPoweredVideo extends HTMLElement {
  constructor() {
    super();

    this.video = this.querySelector('video') || this.querySelector('iframe');
    this.container = this.closest('.video-container') || null;

    if (this.container) this.attachClickListener(this.closest('.video-container'));

    // Visitors who prefer reduced motion should never get autoplaying video.
    // Marshall.Media.initReducedMotion handles the initial pause; here we skip anything that would resume it.
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // check if video is iframe and has a src and that src is youtube
    if (!prefersReducedMotion && this.video.nodeName === 'IFRAME' && this.video.src && this.video.src.includes('youtube') && !this.container) {
      this.video.addEventListener('load', this.onVideoLoad.bind(this));
    }

    // When video is out of viewport pause it. This is to prevent videos from playing in the background.
    // If video is in viewport play it.
    // Use: Intersection Observer API
    const autoplay = this.dataset.autoplay || false;

    // Autoplay heroes have no .video-container UI, so give them an always-visible play/pause
    // toggle. Under reduced motion the media starts paused (play icon); otherwise it starts
    // playing (pause icon). userPaused lets the toggle override the viewport observer below.
    if (autoplay && !this.container) {
      this.userPaused = prefersReducedMotion;
      this.addMediaPlayToggle(prefersReducedMotion);
    }

    if (!autoplay || this.container || prefersReducedMotion) return;

    const videoType = this.video.nodeName === 'VIDEO' ? 'video' : 'iframe';
    const videoHost = this.video.nodeName === 'VIDEO' ? 'shopify' : (this.video.src.includes('youtube') ? 'youtube' : 'vimeo');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Respect an explicit user pause from the toggle.
          if (this.userPaused) return;

          if (videoType == 'video') {
            this.video.play();
          } else if (videoHost == 'youtube') {
            this.video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          } else if (videoHost == 'vimeo') {
            this.video.contentWindow.postMessage('{"method":"play"}', '*');
          }
        } else {
          if (videoType == 'video') {
            this.video.pause()
          } else if (videoHost == 'youtube') {
            this.video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          } else if (videoHost == 'vimeo') {
            this.video.contentWindow.postMessage('{"method":"pause"}', '*');
          }
        }
      });
    }, { threshold: 0.5 });

    observer.observe(this.video);
  }

  attachClickListener(videoContainer) {
    if (!videoContainer) return;

    const mediaWrapper = videoContainer.closest('.video-section__media');

    // The persistent bottom-left play/pause toggle: the cover's button in cover mode, otherwise the
    // snippet's .button-container button. Both use the shared .media-play-toggle style.
    const getToggle = () =>
      (mediaWrapper && mediaWrapper.querySelector('.video-section__play-trigger')) ||
      videoContainer.querySelector('.media-play-toggle');

    // The play toggle only shows while paused; it disappears during playback (native controls remain).
    const hideToggle = () => {
      const toggle = getToggle();
      if (toggle) toggle.hidden = true;
    };
    const showToggle = () => {
      const toggle = getToggle();
      if (toggle) toggle.hidden = false;
    };

    const hideCover = () => {
      const cover = mediaWrapper && mediaWrapper.querySelector('.video-cover');
      if (cover) cover.hidden = true;
    };
    const showCover = () => {
      const cover = mediaWrapper && mediaWrapper.querySelector('.video-cover');
      if (cover) cover.hidden = false;
    };

    const playVideos = () => {
      videoContainer.querySelectorAll('video').forEach(function(video) {
        if (typeof video.play === 'function') video.play();
      });
      videoContainer.querySelectorAll('iframe').forEach(function(iframe) {
        try {
          if (iframe.src && iframe.src.includes('youtube')) {
            iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          } else if (iframe.src && iframe.src.includes('vimeo')) {
            iframe.contentWindow.postMessage('{"method":"play"}', '*');
          }
        } catch (err) {}
      });
    };

    // Cross-origin embeds don't emit play/pause events, so track their state for play/pause + resume.
    const isEmbed = this.video.nodeName === 'IFRAME';
    let embedPaused = true;

    const handlePlayPause = () => {
      const paused = isEmbed ? embedPaused : this.video.paused;

      if (paused) {
        hideCover();
        hideToggle();

        if (isEmbed) {
          playVideos();
          embedPaused = false;
          this.video.dataset.userPlaying = 'true';
        } else {
          this.video.play();
        }
      } else {
        if (isEmbed) {
          Marshall.Media.pauseEmbed(this.video);
          embedPaused = true;
          delete this.video.dataset.userPlaying;
        } else {
          this.video.pause();
        }

        showCover();
        showToggle();
      }
    };

    videoContainer.addEventListener('click', handlePlayPause);

    const cover = mediaWrapper?.querySelector('.video-cover');
    if (cover) {
      cover.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handlePlayPause();
      });
    }
    const playTrigger = mediaWrapper?.querySelector('.video-section__play-trigger');
    if (playTrigger) {
      playTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handlePlayPause();
      });
    }

    // Native <video> fires these (e.g. from its own controls), so keep the toggle + cover in sync.
    this.video.onplay = () => {
      hideCover();
      hideToggle();
    };
    this.video.onpause = () => {
      showCover();
      showToggle();
    };
  }

  onVideoLoad() {
    // force youtube to play on mobile
    this.video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
  }

  // Always-visible play/pause toggle for an autoplay hero (native video or embed) with no
  // .video-container UI. startPaused reflects the initial media state (true under reduced motion).
  addMediaPlayToggle(startPaused) {
    if (this.querySelector(':scope > .media-play-toggle')) return;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'media-play-toggle';
    this.mediaToggle = toggle;
    this.embedPaused = startPaused;
    this.updateMediaToggle(startPaused);

    toggle.addEventListener('click', (event) => {
      // Hero media may be wrapped in a link/overlay; keep the toggle from triggering it. Stopping
      // propagation also prevents the container click handler below from double-firing.
      event.preventDefault();
      event.stopPropagation();
      this.toggleMediaPlayback();
    });
    this.appendChild(toggle);

    // Clicking anywhere on the media toggles playback too, reusing the same handler. Native <video>
    // clicks bubble up to this element; YouTube iframes capture their own clicks (native controls),
    // so body-click toggling applies to native video. Ignore real interactive children so links /
    // buttons keep their own behaviour.
    this.addEventListener('click', (event) => {
      if (event.target.closest('a, button, [role="button"]')) return;

      this.toggleMediaPlayback();
    });

    // Native <video> emits play/pause (e.g. from the viewport observer or overlay pause), so keep
    // the toggle icon in sync with the real state. Cross-origin embeds can't be observed this way.
    if (this.video.nodeName === 'VIDEO') {
      this.video.addEventListener('play', () => this.updateMediaToggle(false));
      this.video.addEventListener('pause', () => this.updateMediaToggle(true));
    }
  }

  updateMediaToggle(paused) {
    if (!this.mediaToggle) return;

    const strings = window.accessibilityStrings || {};
    this.mediaToggle.innerHTML = mediaControlIcon(paused ? 'play' : 'pause');
    this.mediaToggle.setAttribute(
      'aria-label',
      paused ? strings.playVideo || 'Play video' : strings.pauseVideo || 'Pause video'
    );
  }

  // Toggle playback on user request, reusing the shared Marshall.Media primitives.
  toggleMediaPlayback() {
    const isEmbed = this.video.nodeName === 'IFRAME';
    const paused = isEmbed ? this.embedPaused : this.video.paused;

    if (paused) {
      this.userPaused = false;
      if (isEmbed) {
        Marshall.Media.playEmbed(this.video);
        this.embedPaused = false;
        this.video.dataset.userPlaying = 'true';
      } else {
        Marshall.Media.playVideo(this.video);
      }
      this.updateMediaToggle(false);
    } else {
      this.userPaused = true;
      if (isEmbed) {
        Marshall.Media.pauseEmbed(this.video);
        this.embedPaused = true;
        delete this.video.dataset.userPlaying;
      } else {
        Marshall.Media.pauseVideo(this.video);
      }
      this.updateMediaToggle(true);
    }
  }
}
customElements.define('superpowered-video', SuperPoweredVideo);


// BeforeAfterSlider - a custom Marshall built before/after image comparison slider.
// Usage: <before-after-slider>
class BeforeAfterSlider extends HTMLElement {
  constructor() {
    super();

    this.rangeInput = this.querySelector('input[type="range"]');

    if (!this.rangeInput) return;

    this.initSlider();
  }

  initSlider() {
    this.updatePosition(this.rangeInput.value);

    // Input event listener
    this.rangeInput.addEventListener('input', this.onInput.bind(this));

    // Mouse events for better UX
    this.rangeInput.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.rangeInput.addEventListener('mouseup', this.onMouseUp.bind(this));

    // Touch events for mobile
    this.rangeInput.addEventListener('touchstart', this.onTouchStart.bind(this));
    this.rangeInput.addEventListener('touchend', this.onTouchEnd.bind(this));

    // Keyboard navigation
    this.rangeInput.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  updatePosition(value) {
    const numericValue = (value === '0' || value === 0) ? 0 : Math.max(0, Math.min(1000, parseFloat(value) || 500));
    const position = numericValue / 10 + '%';
    this.style.setProperty('--position', position);
  }

  onInput(event) {
    this.updatePosition(event.target.value);
    this.rangeInput.setAttribute('aria-valuetext', `Showing ${event.target.value}% of before image`);
  }

  onMouseDown() {
    document.body.style.userSelect = 'none';
  }

  onMouseUp() {
    document.body.style.userSelect = '';
  }

  onTouchStart() {
    document.body.style.userSelect = 'none';
  }

  onTouchEnd() {
    document.body.style.userSelect = '';
  }

  onKeyDown(event) {
    const min = this.rangeInput.min ? parseInt(this.rangeInput.min) : 0;
    const max = this.rangeInput.max ? parseInt(this.rangeInput.max) : 1000;

    let newValue = parseInt(this.rangeInput.value);

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = Math.max(min, newValue - 50);
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = Math.min(max, newValue + 50);
        break;
      case 'Home':
        newValue = min;
        break;
      case 'End':
        newValue = max;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.rangeInput.value = newValue;
    this.updatePosition(newValue);
  }
}
customElements.define('before-after-slider', BeforeAfterSlider);

// QuantityInput for + and - button functionality
// Used in: cart items, upsell items
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define('quantity-input', QuantityInput);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', this.onVariantChange);
  }

  disconnectedCallback() {
    // Clean up event listeners when the element is removed from the DOM
    this.removeEventListener('change', this.onVariantChange);
  }

  onVariantChange(e) {
    // Check if we need to fetch a different product (for combined listings)
    const { isCombinedProduct, targetUrl } = this.combinedListingCheck(e);

    // Disable the add to cart button while we fetch the new product data
    this.toggleAddButton(true, '');
    const productUrl = targetUrl || this.dataset.url;
    this.updateSelectionMetadata(e);

    // Fetch the product section HTML
    this.fetchProductSection({
      newProductUrl: productUrl,
      callback: isCombinedProduct
        ? this.handleCombinedProduct(targetUrl)
        : this.handleUpdateProductInfo()
    }
    );
  }

  combinedListingCheck(e) {
    const currentUrl = window.location.pathname;
    let targetUrl;

    // For radio buttons, get productUrl from the target
    if (e.target.type === 'radio') {
      targetUrl = e.target.dataset.productUrl;
    }
    // For dropdowns, get productUrl from the selected option
    else if (e.target.tagName === 'SELECT') {
      const selectedOption = e.target.selectedOptions[0];
      targetUrl = selectedOption ? selectedOption.dataset.productUrl : undefined;
    }

    let isCombinedProduct = false;

    if (targetUrl != undefined && currentUrl != targetUrl) {
      isCombinedProduct = true;
    }

    return { isCombinedProduct, targetUrl };
  }

  async fetchProductSection({ newProductUrl, callback }) {
    const params = [];
    const isModal = JSON.parse(this.dataset.modal) || false;
    // Fetch either the full page, main-product section, or ajax-modal template
    if (isModal) {
      params.push('view=ajax-modal');
    } else {
      params.push(`section_id=${this.dataset.section}`);
    }
    this.selectedOptionValues = this.getSelectedOptionValues();
    const selectedOptionValues = this.selectedOptionValues?.join(',');
    params.push(`option_values=${selectedOptionValues}`);

    const requestUrl = `${newProductUrl}?${params.join('&')}`;

    fetch(requestUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        callback(html)
      })
      .catch((error) => {
        console.error('Error fetching product section:', error);
      });
  }

  handleCombinedProduct(newProductUrl) {
    return (html) => {
      const isModal = JSON.parse(this.dataset.modal) || false;
      if (isModal) {
        const modalContent = this.closest('.modal-body');
        modalContent.innerHTML = html.querySelector('.modal-body').innerHTML;
      } else {
        const productElement = this.closest('.main-product');
        productElement.innerHTML = html.querySelector('.main-product').innerHTML;
        window.history.replaceState({}, '', `${newProductUrl}`);
      }
    }
  }

  updateSelectionMetadata(event) {
    const target = event.target;
    const tagName = target.tagName;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');
    }
  }

  getSelectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  handleUpdateProductInfo() {
    return (html) => {
      this.currentVariant = this.getSelectedVariant(html);
      this.updatePickupAvailability();
      this.removeErrorMessage();

      if (!this.currentVariant) {
        this.renderProductInfo(html);
      } else {
        this.updateMedia();
        this.updateURL();
        this.updateVariantInput();
        this.renderProductInfo(html);
        this.updateShareUrl();
      }
    }
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGridySlider = document.getElementById(`slider-${this.dataset.section}`);

    if (mediaGridySlider && mediaGridySlider.emblaApi) {
      const slide = document.getElementById(`item-${this.currentVariant.featured_media.id}`);
      if (slide) {
        const viewport = mediaGridySlider.querySelector('.embla__viewport');
        const slides = viewport && viewport.querySelectorAll('.embla__slide');
        const slideEl = slide.closest ? slide.closest('.embla__slide') : slide;
        const idx = slides ? Array.from(slides).indexOf(slideEl) : -1;
        if (idx >= 0) mediaGridySlider.emblaApi.scrollTo(idx, true);
      }
    }

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const isGrid = mediaGridySlider && (mediaGridySlider.dataset.emblaDesktopEnabled === 'false' || mediaGridySlider.dataset.desktopEnabled === 'false');
    if (isDesktop && isGrid) {
      document.getElementById(`item-${this.currentVariant.featured_media.id}`)?.scrollIntoView();
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}-product-buy-bar`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo(html) {
    const updateSourceFromDestination = (id, shouldHide = (source) => false, syncClassName = false) => {
      const source = html.getElementById(`${id}-${this.dataset.section}`);
      const destination = document.getElementById(`${id}-${this.dataset.section}`);
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        if (syncClassName) {
          destination.className = source.className;
        }
        destination.classList.toggle('hidden', shouldHide(source));
      }
    };

    updateSourceFromDestination('variant-selects');
    updateSourceFromDestination('price');
    updateSourceFromDestination('buy-bar-info');
    updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
    updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
    updateSourceFromDestination('InventoryInline', ({ innerText }) => innerText === '', true);
    updateSourceFromDestination('ProductConfigurationCta');
    updateSourceFromDestination('ProductConfigurationPriceMessage');
    updateSourceFromDestination('ProductConfigurationAdditionalInfo');
    updateSourceFromDestination(
      'badges',
      (source) => source.innerText.trim() === '' && !source.querySelector('[data-price-discount-title]')
    );

    const sourceConfigurationCta = html.getElementById(`ProductConfigurationCta-${this.dataset.section}`);
    const destinationConfigurationCta = document.getElementById(`ProductConfigurationCta-${this.dataset.section}`);
    if (sourceConfigurationCta && destinationConfigurationCta) {
      destinationConfigurationCta.dataset.productState = sourceConfigurationCta.dataset.productState;
      destinationConfigurationCta.dataset.releaseDate = sourceConfigurationCta.dataset.releaseDate;
    }

    const destinationBadges = document.getElementById(`badges-${this.dataset.section}`);
    if (destinationBadges) {
      destinationBadges.closest('.product-buybox-badges')?.classList.toggle('hidden', destinationBadges.classList.contains('hidden'));
    }

    const sourceVariantSelects = html.getElementById(`variant-selects-${this.dataset.section}`);
    if (sourceVariantSelects) {
      this.dataset.isPreorder = sourceVariantSelects.dataset.isPreorder;
    }

    if (this.currentVariant) {
      this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
    } else {
      this.setUnavailable();
    }
  }

  toggleAddButton(disable = true, text) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('.product-form__submit-label-text') || productForm.querySelector('.buy-bar-submit-text') || productForm.querySelector('.product-form__submit-label') || productForm.querySelector('[name="add"] > span');
    const addButtonStock = productForm.querySelector('.product-form__submit-stock');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text && addButtonText) addButtonText.textContent = text;
      if (addButtonStock) addButtonStock.setAttribute('hidden', '');
    } else {
      addButton.removeAttribute('disabled');
      if (this.dataset.isPreorder == 'true') {
        if (addButtonText) addButtonText.textContent = window.variantStrings.preOrder;
      } else {
        if (addButtonText) addButtonText.textContent = window.variantStrings.addToCart;
      }
      if (addButtonStock) addButtonStock.removeAttribute('hidden');
    }
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('.product-form__submit-label-text') || button.querySelector('.buy-bar-submit-text') || button.querySelector('.product-form__submit-label') || button.querySelector('[name="add"] > span');
    const addButtonStock = button.querySelector('.product-form__submit-stock');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    if (addButtonText) addButtonText.textContent = window.variantStrings.unavailable;
    if (addButtonStock) addButtonStock.setAttribute('hidden', '');
    if (price) price.classList.add('hidden');

    const button_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    const addButton_BuyBar = button_BuyBar.querySelector('[name="add"]');
    const addButtonText_BuyBar = button_BuyBar.querySelector('.buy-bar-submit-text') || button_BuyBar.querySelector('.product-form__submit-label-text') || button_BuyBar.querySelector('.product-form__submit-label') || button_BuyBar.querySelector('[name="add"] > span');
    const price_BuyBar = document.getElementById(
      `price-${this.dataset.section}-product-buy-bar`
    );
    if (!addButton_BuyBar) return;
    if (addButtonText_BuyBar) addButtonText_BuyBar.textContent = window.variantStrings.unavailable;
    if (price_BuyBar) price_BuyBar.classList.add("hidden");

    const inventoryStatus = document.getElementById(`Inventory-${this.dataset.section}`);
    if (inventoryStatus) inventoryStatus.classList.add('hidden');

    const inventoryInlineStatus = document.getElementById(`InventoryInline-${this.dataset.section}`);
    if (inventoryInlineStatus) inventoryInlineStatus.classList.add('hidden');

    const badges = document.getElementById(`badges-${this.dataset.section}`);
    if (badges) badges.classList.add('hidden');
  }
}

customElements.define('variant-selects', VariantSelects);

class AudioPlayer extends HTMLElement {
  connectedCallback() {
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleAudioEnd = this.handleAudioEnd.bind(this);

    this.addEventListener('click', this.boundHandleClick);
    this.addEventListener('ended', this.boundHandleAudioEnd, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundHandleClick);
    this.removeEventListener('ended', this.boundHandleAudioEnd, true);
  }

  handleClick(event) {
    const button = event.target.closest('.audio-player__toggle');
    if (!button || !this.contains(button)) return;

    const soundItem = button.closest('.audio-player__item');
    const audio = soundItem ? soundItem.querySelector('.audio-player__audio') : null;
    if (!audio) return;

    const isCurrentlyPlaying = !audio.paused && !audio.ended;
    this.pauseAllSounds();

    if (!isCurrentlyPlaying) {
      const playPromise = audio.play();
      this.setSoundButtonState(button, true);

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          this.setSoundButtonState(button, false);
        });
      }
    }
  }

  handleAudioEnd(event) {
    const audio = event.target;
    if (!audio || !audio.matches('.audio-player__audio')) return;

    const soundItem = audio.closest('.audio-player__item');
    const button = soundItem ? soundItem.querySelector('.audio-player__toggle') : null;
    if (button) this.setSoundButtonState(button, false);
  }

  pauseAllSounds() {
    const allAudio = document.querySelectorAll('.audio-player__audio');
    const allButtons = document.querySelectorAll('.audio-player__toggle');

    allAudio.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });

    allButtons.forEach((button) => this.setSoundButtonState(button, false));
  }

  setSoundButtonState(button, isPlaying) {
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    button.setAttribute('aria-label', isPlaying ? 'Pause sound' : 'Play sound');

    const playIcon = button.querySelector('.audio-player__icon--play');
    const pauseIcon = button.querySelector('.audio-player__icon--pause');

    if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
    if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
  }
}

if (customElements.get('audio-player') === undefined) {
  customElements.define('audio-player', AudioPlayer);
}

class ReadMore extends HTMLElement {
  constructor() {
    super();

    // Cache the read-more element itself for later use
    this.readMoreElement = this;
    this.toggle = this.querySelector('[data-read-more-toggle]');
    this.textElement = this.toggle?.querySelector('[data-read-more-text]');

    // Store bound handlers to avoid creating new functions on each call
    this.boundToggleVisibility = this.toggleVisibility.bind(this);
  }

  connectedCallback() {
    this.toggle.addEventListener('click', this.boundToggleVisibility);
  }

  disconnectedCallback() {
    this.toggle.removeEventListener('click', this.boundToggleVisibility);
  }

  toggleVisibility() {
    this.readMoreElement.classList.toggle('expanded');
    this.toggle.setAttribute('aria-expanded', this.readMoreElement.classList.contains('expanded') ? 'true' : 'false');
    this.toggle.setAttribute('aria-label', this.readMoreElement.classList.contains('expanded') ? this.toggle.getAttribute('data-read-less-label') : this.toggle.getAttribute('data-read-more-label'));
    this.textElement.textContent = this.readMoreElement.classList.contains('expanded') ? this.toggle.getAttribute('data-read-less-label') : this.toggle.getAttribute('data-read-more-label');
  }
}
customElements.define('read-more', ReadMore);
